import { FC, useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, useTheme } from "@mui/material";
import {
  Table,
  types,
  useDispatchOnParams,
  Dropdown,
  utils
} from "@vilocnv/allsetra-core";
import BulkRidesUpdateDriverForm from "components/forms/objects/BulkRidesUpdateDriverForm/BulkRidesUpdateDriverForm";
import BulkRidesUpdateOrderForm from "components/forms/objects/BulkRidesUpdateOrderForm/BulkRidesUpdateOrderForm";
import DetourRouteModal, {
  DetourRouteFormValues
} from "components/modals/DetourRouteModal/DetourRouteModal";
import PrivateMileageModal, {
  PrivateMileageFormValues
} from "components/modals/PrivateMileageModal/PrivateMileageModal";

// Data
import {
  useAppDispatch,
  useAppSelector,
  useResolveAddress,
  useRideActions
} from "hooks";
import { OBJECT_RIDES_TABLE_COLUMNS } from "app/data/constants";
import {
  selectObjectRidesState,
  selectDrawerSelectedAccountId
} from "app/data/selectors";
import {
  getObjectRidesByQueryThunk,
  setObjectSpecificRide
} from "app/features";
import { useTranslation } from "react-i18next";
import {
  DetourIcon,
  EditBoxIcon,
  PrivateMileageIcon,
  ViewOnMapIcon
} from "assets/icons";
import useMovementRides from "hooks/useMovementRides";
import { ObjectRideModeEnum } from "app/data/helpers/objectRideHelpers";
import { getAddressDisplayInfo } from "app/data/helpers";

interface Props {
  specificObject: types.IObject | null;
}

const ObjectRidesSection: FC<Props> = ({ specificObject }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showMovementRides, handleMovementRidesChange } = useMovementRides();

  // Global State
  const drawerSelectedAccountId =
    useAppSelector(selectDrawerSelectedAccountId) ?? "";

  const { objectRides, objectRidesRowCount, ridesLoading } = useAppSelector(
    selectObjectRidesState
  );

  const { resolvedAddresses, resolvedAddressLoading } =
    useResolveAddress(objectRides);

  // Local State
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedRowsIds, setSelectedRowsIds] = useState<Array<string>>([]);
  const [toggledClearRows, setToggleClearRows] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Local State Forms
  const [openBulkModeForm, setOpenBulkModeForm] = useState(false);
  const [openBulkDriverForm, setOpenBulkDriverForm] = useState(false);
  const [openDetourRouteModal, setOpenDetourRouteModal] = useState(false);
  const [openPrivateMileageModal, setOpenPrivateMileageModal] = useState(false);
  const [selectedRide, setSelectedRide] = useState<types.IObjectRide | null>(
    null
  );

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);

  const tableColumns = useMemo(
    () =>
      OBJECT_RIDES_TABLE_COLUMNS(
        t,
        showMovementRides,
        resolvedAddresses,
        resolvedAddressLoading
      ),
    [t, showMovementRides, resolvedAddresses, resolvedAddressLoading]
  );

  useDispatchOnParams(getObjectRidesByQueryThunk, {
    args: {
      accountId: drawerSelectedAccountId,
      objectId: specificObject?.uniqueId || "",
      _refreshTrigger: refreshTrigger // Force refetch when this changes
    },
    additionalParams: {
      includeRidesWithoutDriver: true,
      startedWithoutIgnition: true
    },
    orderBy: [{ field: "startDate", ascending: true }]
  });

  const formattedData = useMemo(() => {
    let reportsData = [...objectRides];
    reportsData = reportsData.filter((object: any) => {
      return showMovementRides ? true : !object.startedWithoutIgnition;
    });
    return reportsData;
  }, [objectRides, showMovementRides]);

  const handleViewOnMap = (ride: types.IObjectRide) => {
    // Check permission before allowing view on map
    if (!canViewRideOnMap(ride)) {
      // Optionally show a toast message
      return;
    }

    dispatch(setObjectSpecificRide(ride));
    navigate(
      `/dashboard/objects/${specificObject?.uniqueId}/rides/map/${ride?.uniqueId}`
    );
  };

  const handleDropdownAnchorClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDropdownAnchorEl(event.currentTarget);
  };

  const onSelectedRowsChange = ({ selectedRows }: any) => {
    const ids = selectedRows?.map((item: any) => item.uniqueId) ?? [];
    setSelectedRowsIds(ids);
  };

  const handleClearRows = () => {
    setSelectedRowsIds([]);
    setToggleClearRows(!toggledClearRows);
  };

  const fetchDataAndResetSelectableRows = () => {
    // Trigger refetch of rides to get updated data after modal success
    setRefreshTrigger((prev) => prev + 1);
    setSelectedRowsIds([]);
    setToggleClearRows(true);
  };

  // Check if user can view ride on map based on coordinates
  const canViewRideOnMap = (ride: types.IObjectRide): boolean => {
    const startAddressInfo = getAddressDisplayInfo(
      ride.startLatitude,
      ride.startLongitude,
      null
    );

    const endAddressInfo = getAddressDisplayInfo(
      ride.endLatitude,
      ride.endLongitude,
      null
    );

    const canViewStart = !startAddressInfo.isHidden;
    const canViewEnd = !endAddressInfo.isHidden;

    return canViewStart || canViewEnd;
  };

  const { addDetourRoute, addPrivateMileage } = useRideActions({
    accountId: drawerSelectedAccountId,
    onSuccess: fetchDataAndResetSelectableRows
  });

  const handleAddDetourRoute = (ride: types.IObjectRide) => {
    setSelectedRide(ride);
    setOpenDetourRouteModal(true);
  };

  const handleAddPrivateMileage = (ride: types.IObjectRide) => {
    setSelectedRide(ride);
    setOpenPrivateMileageModal(true);
  };

  const handleDetourRouteSubmit = async (values: DetourRouteFormValues) => {
    if (!selectedRide) return;
    await addDetourRoute(selectedRide, values);
  };

  const handlePrivateMileageSubmit = async (
    values: PrivateMileageFormValues
  ) => {
    if (!selectedRide) return;
    await addPrivateMileage(selectedRide, values);
  };

  const cellActions = useMemo(
    () => [
      {
        icon: <ViewOnMapIcon />,
        name: t("buttonsText.viewOnmap"),
        onClick: handleViewOnMap,
        when: (row: types.IObjectRide) => canViewRideOnMap(row)
      },
      {
        icon: <DetourIcon />,
        name: t("buttonsText.addDetourMileage"),
        onClick: handleAddDetourRoute,
        when: (row: types.IObjectRide) =>
          row.rideMode !== ObjectRideModeEnum.PRIVATE
      },
      {
        icon: <PrivateMileageIcon />,
        name: t("buttonsText.addPrivateMileage"),
        onClick: handleAddPrivateMileage,
        when: (row: types.IObjectRide) =>
          row.rideMode !== ObjectRideModeEnum.PRIVATE
      }
    ],
    [t]
  );

  return (
    <Box mt={4}>
      <Table
        theme={theme}
        translator={t}
        columns={tableColumns}
        data={formattedData ?? []}
        progressPending={ridesLoading}
        paginationTotalRows={objectRidesRowCount}
        showDateRangeFilter={true}
        onMovementRidesChange={handleMovementRidesChange}
        showToggleMovementRides={true}
        cellActions={cellActions}
        primaryButton={{
          variant: "outlined",
          text: t("buttonsText.cancel"),
          size: "small",
          onClick: handleClearRows,
          disabled: !selectedRowsIds.length,
          theme
        }}
        secondaryButton={{
          disabled:
            ridesLoading ||
            formattedData.length === 0 ||
            !selectedRowsIds.length,
          variant: "contained",
          text: t("buttonsText.updateItems"),
          size: "small",
          endIcon: <EditBoxIcon />,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) =>
            handleDropdownAnchorClick(event),
          theme
        }}
        selectableRows={true}
        onSelectedRowsChange={onSelectedRowsChange}
        clearSelectedRows={toggledClearRows}
      />
      <Dropdown
        anchorEl={dropdownAnchorEl}
        setAnchorEl={setDropdownAnchorEl}
        items={[
          {
            name: t("common.changeRideType"),
            onClick: () => setOpenBulkModeForm(true)
          },
          {
            name: t("common.changeDriver"),
            onClick: () => setOpenBulkDriverForm(true)
          }
        ]}
      />
      {/* Forms */}
      <BulkRidesUpdateOrderForm
        open={openBulkModeForm}
        onClose={() => setOpenBulkModeForm(false)}
        rides={selectedRowsIds}
        afterSubmitActionHandler={fetchDataAndResetSelectableRows}
      />
      <BulkRidesUpdateDriverForm
        open={openBulkDriverForm}
        onClose={() => setOpenBulkDriverForm(false)}
        rides={selectedRowsIds}
        afterSubmitActionHandler={fetchDataAndResetSelectableRows}
      />

      {/* Modals */}
      <DetourRouteModal
        open={openDetourRouteModal}
        onClose={() => {
          setOpenDetourRouteModal(false);
          setSelectedRide(null);
        }}
        ride={selectedRide}
        onSubmit={handleDetourRouteSubmit}
      />
      <PrivateMileageModal
        open={openPrivateMileageModal}
        onClose={() => {
          setOpenPrivateMileageModal(false);
          setSelectedRide(null);
        }}
        ride={selectedRide}
        onSubmit={handlePrivateMileageSubmit}
      />
    </Box>
  );
};

export default ObjectRidesSection;
